import {
  environments,
  modes,
  methods,
  cashierStyles,
  scrollSettings,
  amountDisplayStyle,
  amountTransactionStyle,
  displayAccounts,
  RENDER_LIST_TYPES,
  RENDER_LOGO_OR_NAME,
  ADVANCED_CATEGORIES
} from '@/utils/types'

const booleanOptions = [true, false]

/**
 * Minimum requirement is to set an config object with the key 'val'.
 * Other possible keys: [ label, inputType, options, category, description, fromVersion ]
 * 'inputType' should be set to 'select' together with 'options': [ ...allOptions ] if the option should render as a select/dropdown box.
 * If the options is true/false just set options: booleanOptions.
 *
 * 'category' has to match a category set in the object 'ADVANCED_CATEGORIES' from the file 'types.js'. If you set no category, it will render in
 * the 'Others' section.
 * @author: Håkan Sundström, (hakan.sundstrom@bambora.com)
 * @returns {Object} The initial configs options with val, label, options, description etc..
 */

export const configsInfo = (notStored = false) => ({
  // **************** BASIC CONFIGS ****************
  merchantId: {
    val: notStored ? 1000 : localStorage.getItem('merchantId') || 1000,
    label: 'Merchant ID'
  },
  userId: {
    val: notStored ? 123 : localStorage.getItem('userId') || 123,
    label: 'User ID'
  },
  sessionId: {
    val: 123,
    label: 'Session ID'
  },
  environment: {
    val: environments.TEST,
    label: 'Select environment',
    inputType: 'select',
    options: [
      environments.TEST,
      environments.PRODUCTION,
      environments.DEVELOPMENT
    ]
  },
  method: {
    val: methods.DEPOSIT,
    label: 'Select method',
    inputType: 'select',
    options: [methods.DEPOSIT, methods.WITHDRAWAL]
  },
  // **************** ADVANCED CONFIGS ****************
  containerHeight: {
    category: ADVANCED_CATEGORIES.APPEARANCE,
    val: '85vh',
    label: 'Set container height'
  },
  containerMinHeight: {
    category: ADVANCED_CATEGORIES.APPEARANCE,
    val: '500px',
    label: 'Set container min-height'
  },
  containerWidth: {
    category: ADVANCED_CATEGORIES.APPEARANCE,
    val: '500px',
    label: 'Set container width'
  },
  accountDelete: {
    label: 'Display "delete account" / not',
    val: true,
    inputType: 'select',
    options: booleanOptions
  },
  accountId: {
    label: 'Set accountId',
    val: null
  },
  trackingPixels: {
    val: null
  },
  additionalCashierText: {
    val: true,
    inputType: 'select',
    options: booleanOptions
  },
  allowCancelPendingWithdrawal: {
    label: 'Allow user to cancel a pending withdrawal',
    description: 'Read more about how this works in the docs',
    val: false,
    inputType: 'select',
    options: booleanOptions
  },
  allowUserToDisableCancelWithdrawals: {
    val: false,
    inputType: 'select',
    options: booleanOptions
  },
  alwaysShowSubmitBtn: {
    label: 'Floating submit button',
    description:
      'Show a floating submit button when the real submit button is out of view',
    val: false,
    inputType: 'select',
    options: booleanOptions
  },
  amount: {
    label: 'Set an amount',
    val: null,
    category: ADVANCED_CATEGORIES.AMOUNT
  },
  amountFirst: {
    label: 'Show amount first',
    val: false,
    inputType: 'select',
    options: booleanOptions,
    category: ADVANCED_CATEGORIES.APPEARANCE
  },
  font: {
    label:
      'Set custom font family. E.g: google,Roboto or custom,santander,santander',
    val: '',
    category: ADVANCED_CATEGORIES.APPEARANCE
  },
  customLogoFileName: {
    label: 'Set custom logo filename. E.g: Worldline-Mint-Horizontal.svg',
    val: '',
    category: ADVANCED_CATEGORIES.APPEARANCE
  },
  autoOpenFirstPaymentMethod: {
    label: 'Auto-open the first payment method',
    val: true,
    inputType: 'select',
    options: booleanOptions
  },
  autoProcess: {
    label: 'Auto submit the transaction',
    description:
      'Auto-submit transaction. Requires amount & providerType to be set. Only works for providers that doesn´t require any additional information provided except amount',
    val: false,
    inputType: 'select',
    options: booleanOptions
  },
  bonusCode: {
    label: 'Add a bonus code',
    val: null
  },
  channelId: {
    label: 'Channel ID',
    val: null
  },
  country: {
    label: 'Country',
    val: 'swe'
  },
  disableBtnAtZero: {
    label: 'Disable submit button when amount is 0',
    description: 'Only configurable when in ecommerce mode',
    val: true,
    inputType: 'select',
    options: booleanOptions
  },
  displayLogoOrName: {
    label: 'Display logo / name only',
    val: RENDER_LOGO_OR_NAME.BOTH,
    inputType: 'select',
    options: [
      RENDER_LOGO_OR_NAME.BOTH,
      RENDER_LOGO_OR_NAME.LOGO,
      RENDER_LOGO_OR_NAME.NAME
    ],
    category: ADVANCED_CATEGORIES.APPEARANCE
  },
  errorMsgTxRefId: {
    label: 'Show txRefId in error messages',
    description: 'Show txRefId in error messages. Defaults to false',
    val: false,
    inputType: 'select',
    options: booleanOptions
  },
  feeAmount: {
    val: []
  },
  gaTag: {
    val: null
  },
  globalSubmit: {
    label: 'Display submit button at bottom',
    val: false,
    inputType: 'select',
    options: booleanOptions,
    category: ADVANCED_CATEGORIES.APPEARANCE
  },
  history: {
    label: 'Show tx history in tabs',
    description: 'Must be added with tabs=true',
    val: false,
    inputType: 'select',
    options: booleanOptions,
    category: ADVANCED_CATEGORIES.APPEARANCE
  },
  isMobile: {
    val: false,
    inputType: 'select',
    options: booleanOptions
  },
  layout: {
    label: 'Align vertical/horizontal',
    val: cashierStyles.VERTICAL,
    inputType: 'select',
    options: [cashierStyles.VERTICAL, cashierStyles.HORIZONTAL]
  },
  listRadio: {
    label: 'Use radio buttons',
    val: true,
    inputType: 'select',
    options: booleanOptions,
    category: ADVANCED_CATEGORIES.APPEARANCE
  },
  listType: {
    label: 'Display PM´s as grids or list',
    val: RENDER_LIST_TYPES.LIST,
    inputType: 'select',
    options: [RENDER_LIST_TYPES.LIST, RENDER_LIST_TYPES.GRID],
    category: ADVANCED_CATEGORIES.APPEARANCE
  },
  locale: {
    label: 'Set an locale',
    val: 'en'
  },
  logoFullWidth: {
    label: 'Display logo with 100% width',
    description:
      'When combined with a logoUrl, a header containing the logo is shown',
    val: true,
    inputType: 'select',
    options: booleanOptions
  },
  logoSize: {
    label: 'Logo size',
    description: 'Set size to the passed in logo',
    val: 120
  },
  logoUrl: {
    label: 'Logo URL',
    description: 'Url to logo to show in header',
    val: null
  },
  lookupConfig: {
    label: 'Lookup config',
    description: 'JSON config for the checkout flows lookup',
    val: null
  },
  mode: {
    label: 'gambling, ecommerce or accountVerification',
    val: modes.GAMBLING,
    inputType: 'select',
    options: [modes.GAMBLING, modes.ECOMMERCE, modes.ACCOUNT_VERIFICATION],
    category: ADVANCED_CATEGORIES.APPEARANCE
  },
  pmListLimit: {
    label: 'Display X amount of new PM´s',
    val: 20
  },
  pollLimit: {
    label: 'Number of tries to poll for an updated tx status',
    val: 5
  },
  predefinedAmounts: {
    description: 'Add predefined amounts as (xx,yy,pp,zz)',
    label: 'Predefined amounts',
    val: [200, 500, 1000],
    category: ADVANCED_CATEGORIES.AMOUNT
  },
  showCustomAmountButton: {
    description: 'Show custom amount button',
    label: 'Show custom amount button',
    val: false,
    inputType: 'select',
    options: booleanOptions,
    category: ADVANCED_CATEGORIES.AMOUNT
  },
  predefinedValues: {
    label: 'Hide or show predefined amounts',
    val: true,
    inputType: 'select',
    options: booleanOptions,
    category: ADVANCED_CATEGORIES.AMOUNT
  },
  preventCancelWithdrawal: {
    val: false,
    inputType: 'select',
    options: booleanOptions
  },
  providerType: {
    label: 'Choose a specific PM provider',
    val: null
  },
  receiptAmountDisplayStyle: {
    label: 'Amount symbol style',
    inputType: 'select',
    options: [amountDisplayStyle.SYMBOL, amountDisplayStyle.ISO],
    val: amountDisplayStyle.SYMBOL,
    category: ADVANCED_CATEGORIES.RECEIPT
  },
  receiptAmountTransactionStyle: {
    label: 'Reciept header type',
    description: 'Show receipt header as either Amount or TxAmount',
    inputType: 'select',
    options: [amountTransactionStyle.TXAMOUNT, amountTransactionStyle.AMOUNT],
    val: amountTransactionStyle.TXAMOUNT,
    category: ADVANCED_CATEGORIES.RECEIPT
  },
  receiptExcludeKeys: {
    label: 'Keys which should be excluded on the receipt',
    description:
      'Add values as string, (zz,xx,ii). See available values at the docs',
    val: [],
    category: ADVANCED_CATEGORIES.RECEIPT
  },
  showReceipt: {
    label: 'Show receipt page',
    description:
      'Show receipt page. If false a loader is shown instead. Read more about this option in the docs',
    val: true,
    inputType: 'select',
    options: booleanOptions,
    category: ADVANCED_CATEGORIES.RECEIPT
  },
  saveAccountOption: {
    description: 'Display "save account" option for new payment methods',
    val: false,
    inputType: 'select',
    options: booleanOptions
  },
  scrollToOffset: {
    val: 0
  },
  scrollToPm: {
    val: scrollSettings.NEAREST
  },
  selectlastusedtxmethod: {
    label: 'Auto-select last used PM account',
    val: false,
    inputType: 'select',
    options: booleanOptions
  },
  showAccounts: {
    label: 'How do you want to display saved accounts?',
    val: displayAccounts.LIST_FIRST,
    inputType: 'select',
    options: [
      displayAccounts.LIST_FIRST,
      displayAccounts.LIST_WITH_PM,
      displayAccounts.INLINE,
      displayAccounts.FALSE
    ],
    category: ADVANCED_CATEGORIES.APPEARANCE
  },
  showAmountLimits: {
    label: 'Show amount limits',
    description: 'Show amount limits when set',
    val: false,
    inputType: 'select',
    options: booleanOptions,
    category: ADVANCED_CATEGORIES.AMOUNT
  },
  showBonusCode: {
    label: 'Show bonus code field',
    val: false,
    inputType: 'select',
    options: booleanOptions
  },
  lastUsedDepositAmount: {
    label: 'Use amount from the last successful deposit',
    val: false,
    inputType: 'select',
    options: booleanOptions
  },
  showFee: {
    label: 'Show transactions fee´s',
    val: true,
    inputType: 'select',
    options: booleanOptions
  },
  showFooter: {
    label: 'Display a footer',
    val: true,
    inputType: 'select',
    options: booleanOptions
  },
  showHeader: {
    label: 'Display a header',
    val: true,
    inputType: 'select',
    options: booleanOptions
  },
  showListHeaders: {
    label:
      'Show dividing headers in list view, saved accounts & payment methods',
    val: false,
    inputType: 'select',
    options: booleanOptions
  },
  showTermsConditions: {
    label: 'Show terms & conditions',
    description:
      'Show terms & conditions. Read more about this option in the docs',
    val: false,
    inputType: 'select',
    options: booleanOptions
  },
  showTransactionOverview: {
    label: 'Show transaction amount & fee overview',
    description: 'Show an overview of the amout, fee amount and total',
    val: true,
    inputType: 'select',
    options: booleanOptions,
    category: ADVANCED_CATEGORIES.AMOUNT
  },
  singlePageFlow: {
    label: 'Simplified flow (singlePageFlow)',
    val: false,
    inputType: 'select',
    options: booleanOptions,
    category: ADVANCED_CATEGORIES.APPEARANCE
  },
  storeAccount: {
    val: true,
    inputType: 'select',
    options: booleanOptions
  },
  tabs: {
    label: 'Show nav. tabs',
    val: false,
    inputType: 'select',
    options: booleanOptions,
    category: ADVANCED_CATEGORIES.APPEARANCE
  },
  zebraffeLoader: {
    val: true,
    inputType: 'select',
    options: booleanOptions
  },
  showTransactionsOnly: {
    label: 'Show transactions only',
    description: 'Set to true if you want to land on transactions history page',
    val: false,
    inputType: 'select',
    options: booleanOptions
  },
  hideTxOverview: {
    label: 'Hide transaction overview',
    description: 'Set to true if you want to hide the transaction overview',
    val: false,
    inputType: 'select',
    options: booleanOptions
  },
  allowMobilePopup: {
    label: 'Allow mobile window-popup',
    description:
      "Mobiles defaults to showing providers configured to open in new tabs to instead replace the current window's url. If set to true, a new tab will be opened instead.",
    val: false,
    inputType: 'select',
    options: booleanOptions
  },
  fetchConfig: {
    val: false,
    label: 'Fetch configs from PIQ',
    inputType: 'select',
    options: booleanOptions
  },
  receiptBackBtn: {
    val: false,
    label: 'Show a back button at receipt page. (Available from v1.1.7+)',
    inputType: 'select',
    options: booleanOptions
  },
  pendingTab: {
    val: false,
    label:
      'Add a pending option to "tabs" allowing to navigate to pending transactions view. (Should be used with "tabs=true")',
    inputType: 'select',
    options: booleanOptions
  },
  dropdownTabs: {
    val: false,
    label: 'Display tabs in a dropdown element',
    inputType: 'select',
    options: booleanOptions
  },
  pendingTxHeader: {
    val: true,
    label:
      'Display the header with "pending transactions" when there are pending transactions available',
    inputType: 'select',
    options: booleanOptions
  },
  showtermsAndConditionsTemplate: {
    val: true,
    label:
      'Display the terms and condition template when received from PaymentIQ',
    inputType: 'select',
    options: booleanOptions
  },
  autoSetIframeProviderHeight: {
    val: true,
    label:
      'Set iframe provider height based on {provider}height set in PaymentIQ-BO',
    inputType: 'select',
    options: booleanOptions,
    fromVersion: '1.1.10'
  },
  newPaymentBtn: {
    val: false,
    label: 'Optional "New payment" button at status page',
    inputType: 'select',
    options: booleanOptions,
    fromVersion: '1.1.11'
  },
  blockBrowserNavigation: {
    val: true,
    label: 'Disable native browser back/forward options',
    inputType: 'select',
    options: booleanOptions,
    fromVersion: '1.2.0'
  },
  showAmount: {
    val: true,
    label: 'Display amount options',
    inputType: 'select',
    options: booleanOptions,
    fromVersion: '1.2.0',
    category: ADVANCED_CATEGORIES.AMOUNT
  },
  showCookiesWarning: {
    val: true,
    label: 'Show a warning notice that third-party-cookies are disabled',
    inputType: 'select',
    options: booleanOptions,
    fromVersion: '1.2.0'
  },
  historyDateOptions: {
    val: true,
    label: 'Time range options when viewing the transaction history',
    inputType: 'select',
    options: booleanOptions,
    fromVersion: '1.2.7'
  },
  showBackAtInitPath: {
    val: true,
    label:
      'Display back button when there is a "technical" previous route but current route is the landing route',
    inputType: 'select',
    options: booleanOptions,
    fromVersion: '1.2.8'
  },
  enableTermsTemplateFor: {
    val: null,
    label:
      'Restrict displaying terms & conditions template to only a list of string with selected providers instead of all.',
    fromVersion: '1.2.10'
  },
  fixedProviderType: {
    val: false,
    label:
      'Limit to only render providers specified in "providerType", even when navigating away from the provider',
    inputType: 'select',
    options: booleanOptions,
    fromVersion: '1.2.10'
  },
  prefillCreditcardHolder: {
    label: 'Autopopulate cardholder name',
    description: 'autopopulate the cardholder name and disable cardname field',
    val: false,
    inputType: 'select',
    options: booleanOptions,
    fromVersion: '1.3.4'
  },
  prefillCreditcardHolderEditable: {
    label: 'Autopopulate cardholder name and allow editing',
    description: 'autopopulate the cardholder name and allow editing it',
    val: false,
    inputType: 'select',
    options: booleanOptions,
    fromVersion: '1.14.0'
  }
})
